import {
  defineComponent,
  ref,
  provide,
  reactive,
  toRefs,
  getCurrentInstance,
  onMounted,
  computed,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import PageLoading from '@/components/common/PageLoading.vue';
import NewIndexSiteLayout from '@/components/newIndex/NewIndexSiteLayout.vue';
import Footer from '@/components/layout/footer.vue';
import SideComponent from '@/components/common/SideComponent.vue';
import Loading from '@/components/common/Loading.vue';
import bus from 'vue3-eventbus';
import { getDailyScene, getWeeklyPick, getWeeklyPickDetail } from '@/api/common';
import { mapCuratedArticle } from '@/utils/newIndexMappers';
import { wexinShare, getWechatShareImgUrl } from '@/utils/wxshare.js';

export default defineComponent({
  components: {
    PageLoading,
    NewIndexSiteLayout,
    Footer,
    SideComponent,
    Loading,
  },
  setup() {
    const { proxy } = getCurrentInstance();
    const route = useRoute();
    const router = useRouter();
    const store = useStore();

    const curatedType = computed(() => {
      if (route.meta?.curatedType) return route.meta.curatedType;
      return route.path.includes('weekly-pick') ? 'weekly-pick' : 'daily-scene';
    });

    const weeklyPickId = computed(() => {
      const id = route.params.id;
      if (id == null || id === '') return null;
      const parsed = Number(id);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : id;
    });

    const showWeeklyPickBack = computed(() => curatedType.value === 'weekly-pick');

    const data = reactive({
      isLoading: true,
      isListLoading: true,
      taleDetail: {},
    });

    const showMenu = ref(proxy.$menus);
    provide('showMenu', showMenu);

    const viewBox = ref(null);
    const content = ref(null);

    const scroll = (val) => {
      if (!viewBox.value) return;
      viewBox.value.scrollIntoView({
        behavior: 'smooth',
        block: val === 'top' ? 'start' : 'end',
        inline: 'center',
      });
    };

    const wxshare = (shareData) => {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      let url = `${protocol}//${hostname}`;
      if (hostname.indexOf('www') === -1) {
        url = `${protocol}//www.${hostname}`;
      }
      url += route.fullPath;
      store.dispatch('common/Jssdk', { url }).then((res) => {
        wexinShare(res, {
          title: (shareData.title || '').replace(/<br>/gi, ' '),
          desc: shareData.description || '',
          link: url,
          imgUrl: shareData.thumb || getWechatShareImgUrl(store.state.common.siteInfo),
        });
      });
    };

    const loadArticle = async () => {
      data.isLoading = true;
      data.isListLoading = true;
      let fetcher = curatedType.value === 'weekly-pick' ? getWeeklyPick : getDailyScene;
      if (curatedType.value === 'weekly-pick' && weeklyPickId.value) {
        fetcher = () => getWeeklyPickDetail(weeklyPickId.value);
      }
      try {
        const res = await fetcher();
        const mapped = mapCuratedArticle(res, curatedType.value);
        if (mapped) {
          data.taleDetail = {
            title: mapped.title,
            description: mapped.description,
            content: mapped.content,
            thumb: mapped.thumb,
          };
          wxshare(data.taleDetail);
        } else {
          data.taleDetail = {};
        }
      } catch {
        data.taleDetail = {};
      } finally {
        data.isListLoading = false;
        data.isLoading = false;
        bus.emit('showFooter', true);
      }
    };

    onMounted(() => {
      loadArticle();
    });

    watch(
      () => [route.path, route.params.id],
      () => {
        loadArticle();
      },
    );

    const goWeeklyPickList = () => {
      router.push('/weekly-pick/list');
    };

    return {
      ...toRefs(data),
      viewBox,
      content,
      scroll,
      showWeeklyPickBack,
      goWeeklyPickList,
    };
  },
});
